import { z } from 'zod';
import { id, isoDateTime, localDate } from './common.js';
/**
 * Voice capture. Speaking is far lower-friction than typing when executive
 * function is already spent, so this is the primary input path for the daily
 * journal — the keyboard is the fallback, not the other way round.
 */
export const voiceLogKind = z.enum([
    'daily_journal',
    'thought_record',
    'quick_note',
    'win',
    'vent',
]);
export const transcriptStatus = z.enum(['pending', 'processing', 'done', 'failed', 'skipped']);
export const createVoiceLogMeta = z.object({
    kind: voiceLogKind.default('daily_journal'),
    recordedAt: isoDateTime.optional(),
    durationSeconds: z.number().min(0).max(60 * 60 * 3).optional(),
    title: z.string().max(200).optional(),
    /**
     * Which day this belongs to — the same late-night grace the typed entry has.
     *
     * IT WAS MISSING HERE, and only here, for as long as the grace has existed.
     * A journal spoken at 12:45am went to the new day with no way to say
     * otherwise, while the identical entry typed a minute later was asked which
     * day it belonged to and filed correctly. Speaking is the DEFAULT mode on
     * that screen, so the broken path was the one almost everyone takes, and the
     * working one was the fallback.
     *
     * The server accepts only today or yesterday, so this is a grace and not a
     * date picker — see mayFileUnder.
     */
    day: localDate.optional(),
});
/**
 * Changing an entry's topic words.
 *
 * The whole list, as it should now read: what is missing is removed, what is new
 * is his. The server narrows the app's mark to what is left and never widens it.
 */
export const updateVoiceLogTags = z.object({
    themes: z.array(z.string().trim().min(1).max(40)).max(12),
});
export const retranscribeRequest = z.object({
    voiceLogId: id,
    force: z.boolean().default(false),
});
/**
 * A typed journal entry — same destination as a spoken one, no audio.
 *
 * Voice is the point of this feature, but it cannot be the only way in:
 * sometimes there is someone in the room, or you are on a train, or you simply
 * do not want to hear your own voice back. A journal you can only speak into is
 * one you skip on those days.
 */
export const createTextJournal = z.object({
    kind: voiceLogKind.default('daily_journal'),
    text: z.string().min(1).max(50_000),
    title: z.string().max(200).optional(),
    recordedAt: isoDateTime.optional(),
    /**
     * Which day this belongs to, when that is not the day it was written.
     *
     * `recorded_at` and `day` have always been separate columns — the instant,
     * and the frozen label — but nothing could ever set the label independently,
     * so a journal written at 12:37am about the Saturday that had just ended was
     * filed under Sunday with no way to say otherwise.
     *
     * The server accepts only today or yesterday, so this is a late-night grace
     * and not a date picker.
     */
    day: localDate.optional(),
});
/**
 * Browsers do not agree on recording formats: Chrome and Firefox produce
 * `audio/webm;codecs=opus`, iOS Safari produces `audio/mp4`. Both are accepted
 * and normalised server-side with ffmpeg before transcription.
 */
export const ACCEPTED_AUDIO_MIME = [
    'audio/webm',
    'audio/ogg',
    'audio/mp4',
    'audio/mpeg',
    'audio/wav',
    'audio/x-m4a',
    'audio/aac',
];
export const MAX_AUDIO_BYTES = 100 * 1024 * 1024;
//# sourceMappingURL=voice.js.map